import { useReducer } from 'react';

import { useAccountCompanyId } from 'application/hooks';

import { LicenseType, SubscriptionStatus } from '@entities/billing';
import {
  AcceptanceAdvanceStatus,
  PaymentKind,
  PaymentMethodObjectPureType,
  PaymentScheme,
} from '@shared/api/graphql/generated';

import { LicensesModalAction, LicensesModalState, licensesModalReducer } from './licensesModalReducer';
import { DEFAULT_LICENSES_MODAL_STATE } from './defaultLicensesModalState';
import { useBillingData } from './useBillingData';
import useLicensesModalRestoreState from './useLicensesModalRestoreState';
import createLicensesModalSubscriptionCost from './createLicensesModalSubscriptionCost';
import { getFormatNextBillingDate } from './getFormatNextBillingDate';

interface Props {
  unpaidLicensesOnly: boolean;
  isViewMode: boolean;
  clearCurrentPoint: () => void;
  close: (count?: number) => void;
}

export interface UseLicensesModal {
  state: LicensesModalState;
  dispatch: (value: LicensesModalAction) => void;
  viewMode: boolean;
  totalLicenses: LicenseType[];
  unpaidLicenses: LicenseType[];
  isChangeAutoRenewalButtonDisabled: boolean;
  hasInactiveLicenses: boolean;
  hasTrialLicenses: boolean;
  hasWaitPaymentLicenses: boolean;
  hasGraceLicenses: boolean;
  isNeedPayment: boolean;
  bindCardVisible: boolean;
  isLinkingInProcess: boolean;
  isHintShow: boolean;
  isSubmitLoading: boolean;
  isPendingAcceptanceAdvanced: boolean;
  withResidualPayment: boolean;
  formatNextBillingDate: string;
  paymentMethod: PaymentMethodObjectPureType | null;
  selectedPaymentKind: PaymentKind;
  acceptanceAdvanceStatus: AcceptanceAdvanceStatus | null;
  toEnableAutoRenewalPaid: string[];
  toEnableAutoRenewalUnpaid: string[];
  suspendAutoRenewal: (companyId: string, licensesIds: string[]) => Promise<unknown>;
  resumeAutoRenewal: (companyId: string, licensesIds: string[]) => Promise<unknown>;
  refetchBillingInfo: () => Promise<unknown>;
  getLinkForBindingCard: (uriForReturn: string, companyId: string) => Promise<string | undefined>;
  getLinkForPayment: (uriForReturn: string, companyId: string, licensesIds: string[]) => Promise<string | undefined>;
  setSubscriptionCost: (licensesIds: string[]) => Promise<void>;
  handleLicensesModalClose: () => Promise<void>;
}

const useLicensesModal = ({
  unpaidLicensesOnly,
  isViewMode,
  clearCurrentPoint,
  close,
}: Props): UseLicensesModal | null => {
  const companyId = useAccountCompanyId();

  const [state, dispatch] = useReducer(licensesModalReducer, {
    ...DEFAULT_LICENSES_MODAL_STATE,
    isViewMode,
  });

  const billingData = useBillingData(companyId);

  const { setSubscriptionCost, setExtendSubscriptionCost, resumeSubscriptionCost } =
    createLicensesModalSubscriptionCost({
      companyId,
      billingData,
      dispatch,
    });

  useLicensesModalRestoreState({
    billingData,
    unpaidLicensesOnly,
    dispatch,
    setSubscriptionCost,
    setExtendSubscriptionCost,
  });

  if (!billingData) return null;

  const {
    paymentMethod,
    isLinkingInProcess,
    status,
    nextBillingDate,
    paymentScheme,
    hasTrialLicenses,
    hasInactiveLicenses,
    hasActiveLicenses,
    hasWaitPaymentLicenses,
    hasGraceLicenses,
    unpaidLicenses,
    nonRenewableLicenses,
    totalLicenses,
    unpaidLicensesIds,
    selectedPaymentKind,
    acceptanceAdvanceStatus,

    getLinkForBindingCard,
    getLinkForPayment,
    subscriptionCostLoading,
    suspendAutoRenewal,
    suspendAutoRenewalResult,
    resumeAutoRenewal,
    resumeAutoRenewalResult,
    refetchBillingInfo,
  } = billingData;

  const toEnableAutoRenewalPaid = state.toEnableAutorenewal.filter((id) =>
    nonRenewableLicenses.some((license) => license.id === id)
  );

  const toEnableAutoRenewalUnpaid = state.toEnableAutorenewal.filter((id) =>
    unpaidLicensesIds.some((licenseId) => licenseId === id)
  );

  const isNeedPayment = state.toEnableAutorenewal.length > 0 && state.licensesSubscriptionCost !== 0;

  const bindCardVisible =
    selectedPaymentKind === PaymentKind.Yookassa &&
    hasActiveLicenses &&
    !paymentMethod &&
    !isLinkingInProcess &&
    toEnableAutoRenewalUnpaid.length > 0;

  const isPendingAcceptanceAdvanced =
    selectedPaymentKind === PaymentKind.Sbbol &&
    acceptanceAdvanceStatus === AcceptanceAdvanceStatus.Pending &&
    !hasTrialLicenses;

  const isSubmitUnpaidButtonDisabled =
    state.toEnableAutorenewal.length === 0 ||
    bindCardVisible ||
    (isLinkingInProcess && selectedPaymentKind === PaymentKind.Yookassa) ||
    isPendingAcceptanceAdvanced;

  const isHintShow = state.toSuspendAutorenewal.length === 0 && state.toEnableAutorenewal.length === 0;

  const isSubmitButtonDisabled =
    isHintShow ||
    bindCardVisible ||
    (isLinkingInProcess && selectedPaymentKind === PaymentKind.Yookassa) ||
    isPendingAcceptanceAdvanced;

  const isChangeAutoRenewalButtonDisabled = unpaidLicensesOnly ? isSubmitUnpaidButtonDisabled : isSubmitButtonDisabled;

  const handleLicensesModalClose = async () => {
    if (hasInactiveLicenses) {
      await resumeSubscriptionCost(unpaidLicenses.length);
    }

    clearCurrentPoint();
    close();
  };

  return {
    state,
    dispatch,
    viewMode:
      state.isViewMode &&
      (hasTrialLicenses || (hasActiveLicenses && unpaidLicenses.length === 0 && nonRenewableLicenses.length === 0)),
    totalLicenses,
    unpaidLicenses,
    isChangeAutoRenewalButtonDisabled,
    hasInactiveLicenses,
    hasTrialLicenses,
    hasWaitPaymentLicenses,
    hasGraceLicenses,
    isNeedPayment,
    bindCardVisible,
    isLinkingInProcess: Boolean(isLinkingInProcess) && selectedPaymentKind === PaymentKind.Yookassa,
    isHintShow,
    isSubmitLoading: subscriptionCostLoading || suspendAutoRenewalResult.loading || resumeAutoRenewalResult.loading,
    isPendingAcceptanceAdvanced,
    withResidualPayment:
      paymentScheme === PaymentScheme.InvoiceCalendarMonthly &&
      unpaidLicensesIds.length !== 0 &&
      status !== SubscriptionStatus.TrialSubscription &&
      state.licensesSubscriptionCost !== 0,
    formatNextBillingDate: getFormatNextBillingDate(nextBillingDate, paymentScheme),
    paymentMethod,
    selectedPaymentKind,
    acceptanceAdvanceStatus,
    toEnableAutoRenewalPaid,
    toEnableAutoRenewalUnpaid,
    suspendAutoRenewal,
    resumeAutoRenewal,
    refetchBillingInfo,
    getLinkForBindingCard,
    getLinkForPayment,
    setSubscriptionCost,
    handleLicensesModalClose,
  };
};

export default useLicensesModal;
